import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';
import type { ComponentPageData } from '@/features/content/useComponent';

import { describeStatsFailure } from './statsFailure';
import {
  useRegradeQuestions,
  type RegradeScheme,
  type RegradeSection,
} from './useRegradeQuestions';
import styles from './RegradeQuestionsDialog.module.css';

interface RegradeQuestionsDialogProps {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  onOpenChange(nextOpen: boolean): void;
  open: boolean;
}

const SECTION_LABELS: Record<RegradeSection, string> = {
  homework: 'Homework',
  preLecture: 'Pre-lecture',
};

const SCHEMES: Array<{
  description: string;
  label: string;
  value: RegradeScheme;
}> = [
  {
    description: 'Full points only for a correct answer.',
    label: 'Correctness',
    value: 'correctness',
  },
  {
    description: 'Full points for any answer, correct or not.',
    label: 'Participation',
    value: 'participation',
  },
  {
    description:
      'One point for answering, plus one more point for a correct answer.',
    label: 'Correctness + participation',
    value: 'both',
  },
];

export function RegradeQuestionsDialog({
  component,
  componentId,
  courseId,
  onOpenChange,
  open,
}: RegradeQuestionsDialogProps) {
  const mutation = useRegradeQuestions();

  const availableSections = useMemo<RegradeSection[]>(() => {
    const sections: RegradeSection[] = [];
    if (
      component.instructorStatsSections.some(
        (section) => section.homeworkQuestionCount > 0,
      )
    ) {
      sections.push('homework');
    }
    if (
      component.instructorStatsSections.some(
        (section) => section.preLectureQuestionCount > 0,
      )
    ) {
      sections.push('preLecture');
    }
    return sections;
  }, [component.instructorStatsSections]);

  const [section, setSection] = useState<RegradeSection>(
    availableSections[0] ?? 'homework',
  );
  const [scheme, setScheme] = useState<RegradeScheme>('correctness');
  // The section that was regraded, once the regrade has finished.
  const [regraded, setRegraded] = useState<RegradeSection | null>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  // The dialog has no Dialog.Trigger, so Radix leaves focus on the page when it
  // closes: the element that opened it is kept here and given focus back.
  const openerRef = useRef<HTMLElement | null>(null);

  // Reset the form only on the closed -> open transition so a prior run's
  // selections (or a section that no longer exists) never linger. The guard is
  // essential: `mutation` is a fresh object every render, so an unguarded effect
  // would re-run continuously and wipe the instructor's selections.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setSection(availableSections[0] ?? 'homework');
      setScheme('correctness');
      setRegraded(null);
      mutation.reset();
    }
    wasOpen.current = open;
  }, [availableSections, mutation, open]);

  // The Regrade button goes when the regrade has finished: focus moves to Done.
  useEffect(() => {
    if (regraded) {
      doneRef.current?.focus();
    }
  }, [regraded]);

  async function handleRegrade() {
    try {
      await mutation.mutateAsync({
        component,
        componentId,
        courseId,
        scheme,
        section,
      });
      // It stays open to say so: closing at once left nothing saying it had
      // worked (release QA run 202610230712, GRD-04).
      setRegraded(section);
    } catch {
      // Said below, in words.
    }
  }

  const sectionLabel = SECTION_LABELS[section];
  const schemeLabel =
    SCHEMES.find((option) => option.value === scheme)?.label ?? 'Correctness';

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onCloseAutoFocus={(event) => {
            const opener = openerRef.current;
            if (opener?.isConnected) {
              event.preventDefault();
              opener.focus();
            }
          }}
          onOpenAutoFocus={() => {
            openerRef.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
        >
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>Regrade questions</Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.description}>
                Recompute every student&rsquo;s score for these questions using the
                grading scheme you choose. This changes how the questions are graded
                from now on.
              </p>
            </Dialog.Description>
          </div>

          {regraded ? null : (
            <div className={styles.body}>
              {availableSections.length > 1 ? (
                <fieldset className={styles.fieldset}>
                  <legend className={styles.legend}>Which questions?</legend>
                  {availableSections.map((value) => (
                    <label className={styles.radioLabel} key={value}>
                      <input
                        checked={section === value}
                        name="regrade-section"
                        onChange={() => setSection(value)}
                        type="radio"
                      />
                      <span>{SECTION_LABELS[value]}</span>
                    </label>
                  ))}
                </fieldset>
              ) : null}

              <fieldset className={styles.fieldset}>
                <legend className={styles.legend}>Award points for</legend>
                {SCHEMES.map((option) => (
                  <label className={styles.schemeLabel} key={option.value}>
                    <input
                      checked={scheme === option.value}
                      name="regrade-scheme"
                      onChange={() => setScheme(option.value)}
                      type="radio"
                    />
                    <span className={styles.schemeText}>
                      <span className={styles.schemeName}>{option.label}</span>
                      <span className={styles.schemeDescription}>
                        {option.description}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>

              {/* Warren's ruling on #91 (2026-10-27): a regrade grades answers
                  saved but never checked, as it always has; scores set by hand
                  were always kept. */}
              <p aria-live="polite" className={styles.summary} role="note">
                This regrades every student&rsquo;s <strong>{sectionLabel}</strong>{' '}
                answers for <strong>{schemeLabel}</strong>, including answers they
                saved but never checked. Scores you set by hand are kept.
              </p>
            </div>
          )}

          {/* In the dialog from the start, empty, so its message is read out
              when it comes: a live region that appears together with its text
              is not announced by every screen reader. */}
          <p className={regraded ? styles.summary : styles.statusQuiet} role="status">
            {regraded ? `${SECTION_LABELS[regraded]} scores were regraded for every student.` : null}
          </p>

          {mutation.isError ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {describeStatsFailure(mutation.error, 'The scores were not regraded.')}
            </p>
          ) : null}

          <div className={styles.footer}>
            {regraded ? (
              <Dialog.Close asChild>
                <Button className={styles.regradeButton} ref={doneRef} variant="primary">
                  Done
                </Button>
              </Dialog.Close>
            ) : (
              <>
                <Dialog.Close asChild>
                  <Button className={styles.cancelButton} variant="secondary">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  className={styles.regradeButton}
                  disabled={mutation.isPending || availableSections.length === 0}
                  onClick={() => {
                    void handleRegrade();
                  }}
                  variant="primary"
                >
                  {mutation.isPending ? 'Regrading…' : 'Regrade'}
                </Button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
